import { useEffect, useState } from "react";
import qs from "qs";
import { useLocation, useNavigate } from "react-router";

import { useCustomLocalStorageState } from "shared/hooks";

import { QS_PARSE_ARRAY_LIMIT } from "services/hooks";

import { DEFAULT_TAB, FALLBACK_LOCAL_STORAGE_KEY } from "./constants";
import Tabs from "./Tabs";
import { StatefulTabsProps } from "./types";
import {
  getFirstEnabledTab,
  getIndexFromKey,
  getQueryContentToPersist,
} from "./utils";

const StatefulTabs = ({
  tabs,
  defaultTab = DEFAULT_TAB,
  disableUsingQuery,
  queryParamsToPersist,
  activeTabIndex,
  setActiveTab,
  persistLocalStorageKey,
  ...otherTabsProps
}: StatefulTabsProps) => {
  defaultTab = tabs[defaultTab]?.disabled
    ? getFirstEnabledTab(tabs, DEFAULT_TAB)
    : defaultTab;

  const { tab, ...otherQueryParams } = qs.parse(useLocation().search, {
    ignoreQueryPrefix: true,
    arrayLimit: QS_PARSE_ARRAY_LIMIT,
  });
  const navigate = useNavigate();

  const keys = tabs.map(({ key }) => key);

  const tabQueryIndex = getIndexFromKey(tabs, tab as string, keys);

  const [localStorageIndex, setLocalStorageIndex] =
    useCustomLocalStorageState<number>(
      persistLocalStorageKey ?? FALLBACK_LOCAL_STORAGE_KEY,
      {
        defaultValue: defaultTab,
      }
    );

  const [tabStateIndex, setTabStateIndex] = useState(
    tabQueryIndex !== -1 ? tabQueryIndex : defaultTab
  );

  const setTab = (tab: number = defaultTab) => {
    if (tabs[tab]?.disabled) {
      return;
    }

    if (!disableUsingQuery) {
      const queryContentToPersist = getQueryContentToPersist(
        otherQueryParams,
        queryParamsToPersist
      );

      navigate(
        {
          search: qs.stringify(
            { ...queryContentToPersist, tab: keys[tab] },
            { arrayFormat: "indices" }
          ),
        },
        { replace: true }
      );

      // Also persist to localStorage if a key is provided
      if (persistLocalStorageKey) {
        setLocalStorageIndex(tab);
      }
    } else {
      setTabStateIndex(tab);
      setActiveTab?.(tab);

      // Persist to localStorage if key is provided
      if (persistLocalStorageKey) {
        setLocalStorageIndex(tab);
      }
    }
  };

  const shouldUseLocalStorage =
    persistLocalStorageKey &&
    localStorageIndex !== undefined &&
    !tabs[localStorageIndex]?.disabled &&
    localStorageIndex <= tabs.length - 1;

  const getActiveTabIndex = (): number => {
    // External tab state management (controlled component)
    if (disableUsingQuery) {
      return activeTabIndex !== undefined ? activeTabIndex : tabStateIndex;
    }

    // URL query parameter takes precedence
    if (tabQueryIndex !== -1) {
      return tabQueryIndex;
    }

    // Fallback to local storage if available
    if (shouldUseLocalStorage) {
      return localStorageIndex;
    }

    // Final fallback to default tab
    return defaultTab;
  };

  // persist the tab in the query params in a case when its only saved in local storage
  useEffect(() => {
    if (!disableUsingQuery && shouldUseLocalStorage && tabQueryIndex === -1) {
      navigate(
        {
          search: qs.stringify(
            { tab: keys[localStorageIndex], ...otherQueryParams },
            { arrayFormat: "indices" }
          ),
        },
        { replace: true }
      );
    }
  }, [
    disableUsingQuery,
    persistLocalStorageKey,
    tabQueryIndex,
    localStorageIndex,
    keys,
    otherQueryParams,
    queryParamsToPersist,
    navigate,
    shouldUseLocalStorage,
  ]);

  const tabIndex = getActiveTabIndex();

  return (
    <Tabs
      tabs={tabs}
      activeTabIndex={tabIndex}
      onTabChange={setTab}
      {...otherTabsProps}
    />
  );
};

export default StatefulTabs;
